import { useMemo, useState } from "react";
import type { CorpusCatalogueSubject } from "@shared/types/corpus/api.ts";
import type { TaxonomyUrn } from "@shared/types/taxonomy.ts";
import { getCorpusCatalogueSubjects } from "../../../../lib/corpusApi";
import { useAsyncPaginatedData } from "../../../../hooks/useAsyncPaginatedData";
import { useDebouncedValue } from "../../../../hooks/useDebouncedValue";
import { PAGE_SIZE_OPTIONS, type ListPagination } from "../../../../lib/pagination";
import { useUrlFilters } from "../../../../lib/url_filters";
import { useTaxonomy } from "../../../../context/TaxonomyProvider";
import { catalogueFiltersSchema } from "./catalogueFilters";

export interface UseCatalogueSubjectsResult {
  /** The health field the subjects are narrowed to; null for the whole corpus. */
  field: TaxonomyUrn | null;
  setField: (field: TaxonomyUrn | null) => void;
  /** The search text as typed; the subjects follow it once the typing pauses. */
  query: string;
  setQuery: (query: string) => void;
  subjects: CorpusCatalogueSubject[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  pagination: ListPagination;
}

/** The corpus' main subjects with their paper counts, narrowed to a health field and to a name. */
export function useCatalogueSubjects(): UseCatalogueSubjectsResult {
  const [urlFilters, setFilters] = useUrlFilters(catalogueFiltersSchema);
  const taxonomy = useTaxonomy();
  // A link may name a field the skeleton no longer has; once the skeleton is loaded it is dropped.
  const field = useMemo(
    () =>
      urlFilters.field !== null && !taxonomy.isEmpty && !taxonomy.findField(urlFilters.field)
        ? null
        : urlFilters.field,
    [urlFilters.field, taxonomy]
  );
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 300);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const {
    items: subjects,
    loading,
    error,
    pagination: basePagination,
    refresh,
  } = useAsyncPaginatedData(
    (offsetPage) =>
      getCorpusCatalogueSubjects({
        field: field ?? undefined,
        q: debouncedQuery || undefined,
        ...offsetPage,
      }),
    { pageSize: rowsPerPage, key: `${field ?? ""}:${debouncedQuery}` }
  );

  return {
    field,
    setField: (next) => setFilters({ field: next }),
    query,
    setQuery,
    subjects,
    loading,
    error,
    refresh: () => void refresh(),
    pagination: {
      ...basePagination,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      onPageSizeChange: setRowsPerPage,
    },
  };
}
